import { EAnalyticsEvent } from '@common/analytics-catalog';

import { EAnalyticsGoal } from '../analytics.goals';

// Маппинг вендор-нейтральной цели (EAnalyticsGoal, ею размечен публичный конфигуратор
// для GTM/Метрики) в событие первопартийного каталога + приведение params к его схеме.
// Так внешняя разметка не меняется, а первопартийный поток получает валидные события
// каталога. Цели без первопартийного эквивалента в карту не входят — трекер их отбросит.
export interface IGoalMapping {
    readonly event: EAnalyticsEvent;
    readonly toParams: (raw?: Record<string, unknown>) => Record<string, unknown>;
}

const FEEDBACK_FORM: string = 'feedback';
const CONTACT_FORM: string = 'contact';
const MESSAGE_SHORT_MAX: number = 120;

// Доступ по динамическому ключу: index-signature читаем через переменную, а не литералом
// (иначе dot-notation-автофикс превратит в `raw.key` и споткнётся о TS4111).
function pick(raw: Record<string, unknown> | undefined, key: string): unknown {
    return raw ? raw[key] : undefined;
}

function asString(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }

    // Числа и логические значения приводим, всё прочее в параметры цели не годится.
    return typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

function asNumber(value: unknown): number {
    return typeof value === 'number' ? value : Number(value);
}

/**
 * Таблица goal → первопартийное событие. Ключи — строковые значения {@link EAnalyticsGoal}
 * (лукап по строке из fan-out без каста). Каждое `toParams` приводит произвольные params
 * цели к схеме каталога (ключи-провода snake_case), отбрасывая лишние поля и PII.
 */
// Ключ приходит из вызова снаружи и в карте может отсутствовать — значение
// объявлено необязательным, иначе проверка ниже выглядела бы лишней.
export const GOAL_TO_CATALOG: Readonly<Partial<Record<string, IGoalMapping>>> = {
    [EAnalyticsGoal.CalculatorOpen]: {
        event: EAnalyticsEvent.CalculatorOpen,
        toParams: (): Record<string, unknown> => ({}),
    },
    [EAnalyticsGoal.CalculatorParamChange]: {
        event: EAnalyticsEvent.CalculatorParamChange,
        toParams: (raw?: Record<string, unknown>): Record<string, unknown> => ({
            field: asString(pick(raw, 'field')),
            value: pick(raw, 'value'),
        }),
    },
    [EAnalyticsGoal.CalculatorResult]: {
        event: EAnalyticsEvent.CalculatorResult,
        toParams: (raw?: Record<string, unknown>): Record<string, unknown> => ({
            step_count: asNumber(pick(raw, 'step_count')),
            total_run: asNumber(pick(raw, 'total_run')),
            value: asNumber(pick(raw, 'total_cost')),
            currency: asString(pick(raw, 'currency')),
        }),
    },
    [EAnalyticsGoal.ExportClick]: {
        event: EAnalyticsEvent.PdfDownload,
        toParams: (raw?: Record<string, unknown>): Record<string, unknown> => ({
            file_type: asString(pick(raw, 'kind')),
            stair_type: asString(pick(raw, 'stair_type')),
        }),
    },
    [EAnalyticsGoal.ShareClick]: {
        event: EAnalyticsEvent.ShareClick,
        toParams: (raw?: Record<string, unknown>): Record<string, unknown> => ({
            channel: asString(pick(raw, 'channel')),
        }),
    },
    [EAnalyticsGoal.FeedbackOpen]: {
        event: EAnalyticsEvent.FormOpen,
        toParams: (): Record<string, unknown> => ({ form_type: FEEDBACK_FORM }),
    },
    [EAnalyticsGoal.FeedbackSubmitSuccess]: {
        event: EAnalyticsEvent.FormSubmitSuccess,
        toParams: (): Record<string, unknown> => ({ form_type: FEEDBACK_FORM }),
    },
    [EAnalyticsGoal.FeedbackSubmitError]: {
        event: EAnalyticsEvent.FormSubmitError,
        toParams: (raw?: Record<string, unknown>): Record<string, unknown> => ({
            form_type: FEEDBACK_FORM,
            error_code: asString(pick(raw, 'reason')),
        }),
    },
    [EAnalyticsGoal.ContactOpen]: {
        event: EAnalyticsEvent.FormOpen,
        toParams: (): Record<string, unknown> => ({ form_type: CONTACT_FORM }),
    },
    [EAnalyticsGoal.ContactSubmitSuccess]: {
        event: EAnalyticsEvent.FormSubmitSuccess,
        toParams: (): Record<string, unknown> => ({ form_type: CONTACT_FORM }),
    },
    [EAnalyticsGoal.ContactSubmitError]: {
        event: EAnalyticsEvent.FormSubmitError,
        toParams: (raw?: Record<string, unknown>): Record<string, unknown> => ({
            form_type: CONTACT_FORM,
            error_code: asString(pick(raw, 'reason')),
        }),
    },
    [EAnalyticsGoal.AppError]: {
        event: EAnalyticsEvent.Error,
        toParams: (raw?: Record<string, unknown>): Record<string, unknown> => ({
            error_type: 'app',
            message_short: asString(pick(raw, 'message')).slice(0, MESSAGE_SHORT_MAX),
        }),
    },
};
